import { Component, OnInit } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { HttpHelpService } from "../../service/http-help.service";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";

/**
 * @class HistoryComponent
 * @description Component responsible for displaying the history of transaction zip files.
 * It allows users to view, search, and filter transaction history.
 */
@Component({
	selector: "app-history",
	templateUrl: "./history.component.html",
	styleUrls: ["./history.component.scss"]
})
export class HistoryComponent implements OnInit {
	/**
	 * @property {string} curentLang
	 * @description Stores the current language setting for the application.
	 * Initialized from localStorage.
	 */
	curentLang: string = "";
	/**
	 * @property {any} layoutVersion
	 * @description Stores the current layout version (e.g., 'light-only', 'dark-only').
	 * Initialized from localStorage and defaults to 'light-only'.
	 */
	layoutVersion: any;
	/**
	 * @property {any} userRegistId
	 * @description Stores the registration ID of the current user.
	 * Initialized from localStorage.
	 */
	userRegistId: any = JSON.parse(localStorage.getItem('user_id'));
	/**
	 * @property {any} merchantId
	 * @description Stores the ID of the current merchant.
	 * Initialized from localStorage.
	 */
	merchantId = JSON.parse(localStorage.getItem('merchant_id'));
	/**
	 * @property {any} user_type
	 * @description Stores the type of the current user.
	 * Initialized from localStorage.
	 */
	user_type = JSON.parse(localStorage.getItem('user_type'));
	/**
	 * @property {string} term
	 * @description Stores the search term used for filtering transactions.
	 */
	term: string = "";
	/**
	 * @property {any} p
	 * @description Used for pagination control, likely representing the current page number.
	 */
	p: any;
	/**
	 * @property {any[]} TransactionsData
	 * @description Array to store all transaction history data fetched from the server.
	 */
	TransactionsData: any[] = [];
	/**
	 * @property {any[]} filteredTransactionsData
	 * @description Array to store the transactions data after applying filters based on the search term.
	 */
	filteredTransactionsData: any[] = [];

	/**
	 * @constructor
	 * @param {NgxSpinnerService} spinner - Service for displaying loading spinners.
	 * @param {TranslateService} translate - Service for handling internationalization and localization.
	 * @param {HttpHelpService} service - Custom service for making HTTP requests.
	 * @param {NgbModal} modalService - Service for managing modal dialogs.
	 */
	constructor(
		private spinner: NgxSpinnerService,
		public translate: TranslateService,
		private service: HttpHelpService,
	) {
		this.curentLang = localStorage.getItem('curentLang');
		this.merchantId = JSON.parse(localStorage.getItem('merchant_id'));
		this.user_type = JSON.parse(localStorage.getItem('user_type'));
	}

	/**
	 * @method getAllTransactionsHistoryZipFile
	 * @description Fetches the history of all transaction zip files from the server.
	 * It shows a spinner during the API call and hides it upon completion or error.
	 * Populates `TransactionsData` with the response and then calls `filterTransactions`.
	 * @returns {void}
	 */
	getAllTransactionsHistoryZipFile() {
		this.spinner.show();
		this.service.getAllTransactionsHistoryZipFile().subscribe({
			next: res => {
				this.TransactionsData = res.data;

				this.filterTransactions();
				this.spinner.hide();
			},
			error: error => {
				this.spinner.hide();
			}
		});
	};

	/**
	 * @method filterTransactions
	 * @description Filters the `TransactionsData` array based on the `term` property.
	 * The filter checks against various properties of each transaction object, including
	 * user name, company name, merchant name, type of operation, success count,
	 * failure count, and creation date.
	 * If `term` is empty, `filteredTransactionsData` will be a copy of `TransactionsData`.
	 * @returns {void}
	 */
	filterTransactions() {
		if (this.term) {
			this.filteredTransactionsData = this.TransactionsData.filter(transaction =>
				transaction.createdBy.user_name.toLowerCase().includes(this.term.toLowerCase()) ||
				transaction.company.company_name_en.toLowerCase().includes(this.term.toLowerCase()) ||
				transaction.company.merchant.merchant_name_en.toLowerCase().includes(this.term.toLowerCase()) ||
				transaction.Type_of_operation.toLowerCase().includes(this.term.toLowerCase()) ||
				transaction.success_count.toString().includes(this.term) ||
				transaction.failure_count.toString().includes(this.term) ||
				transaction.createdDate.toLowerCase().includes(this.term.toLowerCase())
			);
		} else {
			this.filteredTransactionsData = [...this.TransactionsData];
		}
	}

	/**
	 * @method ngOnInit
	 * @description Angular lifecycle hook that is called after data-bound properties of a directive are initialized.
	 * It calls `getAllTransactionsHistoryZipFile` to fetch initial data.
	 * It also initializes `layoutVersion` from localStorage, defaulting to 'light-only' and applying the
	 * corresponding CSS class to the document body.
	 * Re-initializes `userRegistId`, `merchantId`, and `user_type` from localStorage.
	 * @returns {void}
	 */
	ngOnInit() {
		this.getAllTransactionsHistoryZipFile();
		this.layoutVersion = localStorage.getItem("layout_version");
		if (this.layoutVersion == null || this.layoutVersion == undefined) {
			localStorage.setItem("layout_version", "light-only");
			this.layoutVersion = "light-only";
		} else {
			this.layoutVersion = localStorage.getItem("layout_version");
			if (this.layoutVersion === "dark-only") {
				document.body.classList.add("dark-only");
			} else {
				document.body.classList.add("light-only");
			}
		}
		this.userRegistId = JSON.parse(localStorage.getItem('user_id'));
		this.merchantId = JSON.parse(localStorage.getItem('merchant_id'));
		this.user_type = JSON.parse(localStorage.getItem('user_type'));
	}
}
